import React from "react";
import { Truck } from "lucide-react";
import { formatDateTime, formatKey, formatValue } from "../utils/responseParser";

/**
 * ShipmentCard - Renders shipment data in a structured card format
 * @param {Object} props
 * @param {Object} props.data - The shipment data object
 * @param {Function} props.onClick - Optional callback when card is clicked
 * @returns {JSX.Element}
 */
const ShipmentCard = ({ data, onClick }) => {
    if (!data || typeof data !== "object") return null;

    // Status color mapping
    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-700";
        const s = status.toLowerCase();
        if (s.includes("transit") || s.includes("in transit")) return "bg-blue-100 text-blue-700";
        if (s.includes("deliver")) return "bg-green-100 text-green-700";
        if (s.includes("pend")) return "bg-amber-100 text-amber-700";
        if (s.includes("cancel") || s.includes("exception")) return "bg-red-100 text-red-700";
        return "bg-gray-100 text-gray-700";
    };

    // Extract common shipment fields with fallback to any available data
    const shipmentNumber = data.shipmentNumber || data.shipment_number || data.shipmentId || data.shipment_id || data.id || "N/A";
    const status = data.status || data.shipmentStatus || data.shipment_status || "Unknown";
    const origin = data.origin || data.originLocation || data.origin_location || data.pickupLocation || data.pickup_location || "-";
    const destination = data.destination || data.destinationLocation || data.destination_location || data.deliveryLocation || data.delivery_location || "-";
    const pickupDate = data.pickupDate || data.pickup_date || data.pickup || data.pickupDateTime || data.pickup_date_time || null;
    const estimatedDelivery = data.estimatedDelivery || data.estimated_delivery || data.eta || data.estimatedDeliveryDate || data.estimated_delivery_date || null;
    const actualDelivery = data.actualDelivery || data.actual_delivery || data.deliveryDate || data.delivery_date || data.deliveredAt || data.delivered_at || null;
    const carrierName = data.carrierName || data.carrier_name || data.carrier || null;
    const equipmentNumber = data.equipmentNumber || data.equipment_number || data.equipment || data.containerNumber || data.container_number || null;
    const isInvoiced = data.isInvoiced || data.is_invoiced || data.invoiced || false;

    // Get additional fields to display
    const getAdditionalFields = () => {
        const excludeKeys = [
            "shipmentNumber", "shipment_number", "shipmentId", "shipment_id", "id",
            "status", "shipmentStatus", "shipment_status",
            "origin", "originLocation", "origin_location", "pickupLocation", "pickup_location",
            "destination", "destinationLocation", "destination_location", "deliveryLocation", "delivery_location",
            "pickupDate", "pickup_date", "pickup", "pickupDateTime", "pickup_date_time",
            "estimatedDelivery", "estimated_delivery", "eta", "estimatedDeliveryDate", "estimated_delivery_date",
            "actualDelivery", "actual_delivery", "deliveryDate", "delivery_date", "deliveredAt", "delivered_at",
            "carrierName", "carrier_name", "carrier",
            "equipmentNumber", "equipment_number", "equipment", "containerNumber", "container_number",
            "isInvoiced", "is_invoiced", "invoiced"
        ];
        const additional = {};
        for (const [key, value] of Object.entries(data)) {
            if (!excludeKeys.includes(key) && value !== null && value !== undefined) {
                additional[key] = value;
            }
        }
        return additional;
    };

    const additionalFields = getAdditionalFields();

    return (
        <div
            className={`mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden transition-all ${
                onClick ? "cursor-pointer hover:shadow-md hover:border-acs-primary-green" : ""
            }`}
            onClick={() => onClick && onClick(data)}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <Truck className="w-4 h-4 text-acs-primary-green" />
                <span className="text-sm font-semibold text-gray-800 flex-1">
                    Shipment {shipmentNumber}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(status)}`}>
                    {status}
                </span>
            </div>

            {/* Body */}
            <div className="p-3">
                {/* Route info */}
                <div className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg mb-3">
                    <div className="flex-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                        <div className="text-sm font-semibold text-gray-800">{origin}</div>
                    </div>
                    <span className="text-xl text-gray-400">→</span>
                    <div className="flex-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">To</span>
                        <div className="text-sm font-semibold text-gray-800">{destination}</div>
                    </div>
                </div>

                {/* Details */}
                <div className="space-y-1.5">
                    {pickupDate && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Pickup</span>
                            <span className="font-medium text-gray-800">{formatDateTime(pickupDate)}</span>
                        </div>
                    )}
                    {estimatedDelivery && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Est. Delivery</span>
                            <span className="font-medium text-gray-800">{formatDateTime(estimatedDelivery)}</span>
                        </div>
                    )}
                    {actualDelivery && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Actual Delivery</span>
                            <span className="font-medium text-gray-800">{formatDateTime(actualDelivery)}</span>
                        </div>
                    )}
                    {carrierName && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Carrier</span>
                            <span className="font-medium text-gray-800">{carrierName}</span>
                        </div>
                    )}
                    {equipmentNumber && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Equipment</span>
                            <span className="font-medium text-gray-800">{equipmentNumber}</span>
                        </div>
                    )}
                </div>

                {/* Additional fields */}
                {Object.keys(additionalFields).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                        {Object.entries(additionalFields).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-xs">
                                <span className="text-gray-500">{formatKey(key)}</span>
                                <span className="font-medium text-gray-800">{formatValue(value)}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Invoiced badge */}
                {isInvoiced && (
                    <div className="mt-2 inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-medium rounded">
                        Invoiced
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShipmentCard;
